// ============================================================
// COMPONENTE: ANTES DE VIAJAR
// ============================================================
// Info curada por destino: documentación, apps, dinero (con tipo
// de cambio en vivo), idioma, conectividad. + Notas personales
// del usuario guardadas en backend.
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "../../hooks/UseMediaQuery";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import WifiIcon from "@mui/icons-material/Wifi";
import SimCardIcon from "@mui/icons-material/SimCard";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import TranslateIcon from "@mui/icons-material/Translate";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Datos de ejemplo
export const defaultCityData = {
	cityName: "Mi viaje",
	accentColor: "#6366F1",
	accentColorLight: "rgba(99, 102, 241, 0.1)",
	documentation: {
		visa: "Consulta los requisitos de visado para tu destino.",
		insurance: "Recomendado contratar seguro de viaje.",
		vaccinations: "Consulta vacunas recomendadas.",
	},
	packing: [],
	apps: [],
	connectivity: { sim: "", esim: "", wifi: "" },
	money: {
		currency: "",
		currencyCode: "",
		exchangeRate: "",
		cards: "",
		atm: "",
		tip: "",
	},
	language: {
		main: "",
		english: "",
		phrases: [],
	},
	weather: {
		description: "",
		bestTime: "",
		avoid: "",
	},
};

// Mapa de currency code para la API de exchange rate
const CURRENCY_CODES = {
	tokio: "JPY",
	kioto: "JPY",
	paris: "EUR",
	londres: "GBP",
	ny: "USD",
	seul: "KRW",
	sanFrancisco: "USD",
};

export default function BeforeYouTravel({
	direction,
	cityData: propsCityData,
	projectId,
	api,
}) {
	const isMobile = useMediaQuery("(max-width: 600px)");
	const [activeSection, setActiveSection] = useState("docs");
	const cityData = propsCityData || defaultCityData;
	const { accentColor, accentColorLight, cityName } = cityData;

	// Notas personales
	const [notes, setNotes] = useState({});
	const [editingNote, setEditingNote] = useState(null);
	const [noteText, setNoteText] = useState("");
	const saveTimeoutRef = useRef(null);

	// Tipo de cambio en vivo
	const [liveRate, setLiveRate] = useState(null);
	const [rateLoading, setRateLoading] = useState(false);

	// Detectar currencyCode del destino
	const currencyCode =
		cityData.money?.currencyCode || CURRENCY_CODES[cityData.cityId] || "";

	// Cargar notas del backend
	useEffect(() => {
		if (!projectId || !api) return;
		api
			.get(`/travel-notes/${projectId}`)
			.then((data) => {
				if (data && typeof data === "object") setNotes(data);
			})
			.catch((err) => {
				console.error("Error cargando notas:", err);
				const saved = localStorage.getItem(`travel-notes-${projectId}`);
				if (saved) setNotes(JSON.parse(saved));
			});
	}, [projectId, api]);

	// Fallback localStorage
	useEffect(() => {
		if (projectId && Object.keys(notes).length > 0) {
			localStorage.setItem(`travel-notes-${projectId}`, JSON.stringify(notes));
		}
	}, [notes, projectId]);

	// Cargar tipo de cambio en vivo
	useEffect(() => {
		if (!currencyCode || currencyCode === "EUR") return;
		setRateLoading(true);
		fetch(`https://open.er-api.com/v6/latest/EUR`)
			.then((r) => r.json())
			.then((data) => {
				if (data.result === "success" && data.rates?.[currencyCode]) {
					setLiveRate({
						rate: data.rates[currencyCode],
						code: currencyCode,
						date: data.time_last_update_utc,
					});
				}
			})
			.catch((err) => console.error("Error tipo de cambio:", err))
			.finally(() => setRateLoading(false));
	}, [currencyCode]);

	// Guardar nota
	const handleSaveNote = useCallback(
		async (section) => {
			const content = noteText.trim();
			setNotes((prev) => ({
				...prev,
				[section]: { ...prev[section], content },
			}));
			setEditingNote(null);

			if (projectId && api) {
				clearTimeout(saveTimeoutRef.current);
				saveTimeoutRef.current = setTimeout(async () => {
					try {
						await api.post(`/travel-notes/${projectId}`, {
							section,
							content,
						});
					} catch (err) {
						console.error("Error guardando nota:", err);
					}
				}, 300);
			}
		},
		[noteText, projectId, api]
	);

	// Empezar a editar nota
	const startEditNote = useCallback(
		(section) => {
			setEditingNote(section);
			setNoteText(notes[section]?.content || "");
		},
		[notes]
	);

	const sections = [
		{ id: "docs", Icon: DescriptionIcon, label: "Info" },
		{ id: "apps", Icon: PhoneIphoneIcon, label: "Apps" },
		{ id: "money", Icon: AccountBalanceWalletIcon, label: "Dinero" },
		{ id: "language", Icon: TranslateIcon, label: "Idioma" },
	];

	return (
		<motion.div
			initial={{ opacity: 0, x: direction === "next" ? 100 : -100 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: direction === "next" ? -100 : 100 }}
			transition={{ duration: 0.5 }}
			style={{
				height: "100vh",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					backgroundColor: "#f7f7f7",
					fontFamily: "'Poppins', sans-serif",
				}}
			>
				{/* Header */}
				<div
					style={{
						backgroundColor: accentColor,
						color: "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 8,
					}}
				>
					<FlightTakeoffIcon sx={{ fontSize: 20 }} />
					Antes de viajar a {cityName}
				</div>

				{/* Sub-tabs */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-around",
						padding: "10px 8px",
						backgroundColor: "#fff",
						borderBottom: "2px solid #eee",
						flexShrink: 0,
					}}
				>
					{sections.map((section) => {
						const SIcon = section.Icon;
						return (
							<button
								key={section.id}
								onClick={() => setActiveSection(section.id)}
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									gap: 4,
									padding: "8px 12px",
									border: "none",
									borderRadius: 12,
									backgroundColor:
										activeSection === section.id
											? accentColorLight
											: "transparent",
									cursor: "pointer",
									transition: "all 0.2s",
								}}
							>
								<SIcon
									sx={{
										fontSize: 22,
										color: activeSection === section.id ? accentColor : "#888",
									}}
								/>
								<span
									style={{
										fontSize: 10,
										fontWeight: activeSection === section.id ? 700 : 500,
										color: activeSection === section.id ? accentColor : "#666",
									}}
								>
									{section.label}
								</span>
							</button>
						);
					})}
				</div>

				<div
					style={{
						flex: 1,
						overflowY: "auto",
						padding: "16px",
						display: "flex",
						flexDirection: "column",
						gap: 12,
					}}
				>
					{activeSection === "docs" && (
						<DocumentationSection
							data={cityData}
							note={notes.docs}
							editingNote={editingNote}
							noteText={noteText}
							onStartEdit={() => startEditNote("docs")}
							onSaveNote={() => handleSaveNote("docs")}
							onCancelNote={() => setEditingNote(null)}
							onChangeNote={setNoteText}
						/>
					)}
					{activeSection === "apps" && (
						<AppsSection
							data={cityData}
							note={notes.apps}
							editingNote={editingNote}
							noteText={noteText}
							onStartEdit={() => startEditNote("apps")}
							onSaveNote={() => handleSaveNote("apps")}
							onCancelNote={() => setEditingNote(null)}
							onChangeNote={setNoteText}
						/>
					)}
					{activeSection === "money" && (
						<MoneySection
							data={cityData}
							liveRate={liveRate}
							rateLoading={rateLoading}
							note={notes.money}
							editingNote={editingNote}
							noteText={noteText}
							onStartEdit={() => startEditNote("money")}
							onSaveNote={() => handleSaveNote("money")}
							onCancelNote={() => setEditingNote(null)}
							onChangeNote={setNoteText}
						/>
					)}
					{activeSection === "language" && (
						<LanguageSection
							data={cityData}
							note={notes.language}
							editingNote={editingNote}
							noteText={noteText}
							onStartEdit={() => startEditNote("language")}
							onSaveNote={() => handleSaveNote("language")}
							onCancelNote={() => setEditingNote(null)}
							onChangeNote={setNoteText}
						/>
					)}
				</div>
			</div>
		</motion.div>
	);
}

// ── Sección Documentación ──
function DocumentationSection({
	data,
	note,
	editingNote,
	noteText,
	onStartEdit,
	onSaveNote,
	onCancelNote,
	onChangeNote,
}) {
	const {
		documentation,
		weather,
		connectivity,
		accentColor,
		accentColorLight,
	} = data;
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<InfoCard
				Icon={DescriptionIcon}
				title="Visado"
				content={documentation.visa}
				accentColor={accentColor}
			/>
			<InfoCard
				Icon={LocalHospitalIcon}
				title="Seguro"
				content={documentation.insurance}
				accentColor={accentColor}
			/>
			<InfoCard
				Icon={VaccinesIcon}
				title="Vacunas"
				content={documentation.vaccinations}
				accentColor={accentColor}
			/>

			{/* Clima */}
			{weather?.description && (
				<div
					style={{
						backgroundColor: accentColorLight,
						border: `2px solid ${accentColor}`,
						borderRadius: 16,
						padding: 16,
					}}
				>
					<h4
						style={{
							...h4Style,
							color: accentColor,
							display: "flex",
							alignItems: "center",
							gap: 6,
						}}
					>
						<ThermostatIcon sx={{ fontSize: 18 }} /> Clima
					</h4>
					<p style={pStyle}>{weather.description}</p>
					{weather.bestTime && (
						<p
							style={{
								...pStyle,
								marginTop: 8,
								fontWeight: 600,
								color: "#10B981",
								display: "flex",
								alignItems: "center",
								gap: 6,
							}}
						>
							<EventAvailableIcon sx={{ fontSize: 16 }} /> {weather.bestTime}
						</p>
					)}
					{weather.avoid && (
						<p
							style={{
								...pStyle,
								marginTop: 4,
								color: "#EF4444",
								display: "flex",
								alignItems: "center",
								gap: 6,
							}}
						>
							<WarningAmberIcon sx={{ fontSize: 16 }} /> {weather.avoid}
						</p>
					)}
				</div>
			)}

			{/* Conectividad */}
			{connectivity?.sim && (
				<InfoCard
					Icon={SimCardIcon}
					title="Internet y SIM"
					content={`${connectivity.sim}${
						connectivity.esim ? `\n\n${connectivity.esim}` : ""
					}`}
					accentColor={accentColor}
				/>
			)}

			{/* Nota personal */}
			<PersonalNote
				section="docs"
				note={note}
				editingNote={editingNote}
				noteText={noteText}
				onStartEdit={onStartEdit}
				onSave={onSaveNote}
				onCancel={onCancelNote}
				onChange={onChangeNote}
				accentColor={accentColor}
				accentColorLight={accentColorLight}
				placeholder="Ej: Mi seguro es IATI, póliza nº 12345..."
			/>
		</div>
	);
}

// ── Sección Apps ──
function AppsSection({
	data,
	note,
	editingNote,
	noteText,
	onStartEdit,
	onSaveNote,
	onCancelNote,
	onChangeNote,
}) {
	const { apps, connectivity, accentColor, accentColorLight } = data;
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
			<p style={{ ...pStyle, marginBottom: 4, color: "#666" }}>
				Apps imprescindibles:
			</p>
			{apps.map((app, index) => (
				<div
					key={index}
					style={{
						backgroundColor: "#fff",
						borderRadius: 12,
						padding: "12px 14px",
						display: "flex",
						alignItems: "center",
						gap: 12,
						border: "2px solid #eee",
					}}
				>
					<div
						style={{
							width: 40,
							height: 40,
							borderRadius: 10,
							backgroundColor: accentColorLight,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<PhoneIphoneIcon sx={{ fontSize: 20, color: accentColor }} />
					</div>
					<div>
						<p style={{ ...pStyle, fontWeight: 600 }}>{app.name}</p>
						<p
							style={{
								...pStyle,
								fontSize: 11,
								color: "#888",
							}}
						>
							{app.description}
						</p>
					</div>
				</div>
			))}
			{connectivity?.wifi && (
				<div
					style={{
						backgroundColor: accentColorLight,
						border: `2px solid ${accentColor}`,
						borderRadius: 12,
						padding: 12,
						marginTop: 4,
						display: "flex",
						alignItems: "center",
						gap: 8,
					}}
				>
					<WifiIcon sx={{ fontSize: 18, color: accentColor }} />
					<p style={{ ...pStyle, fontSize: 12 }}>{connectivity.wifi}</p>
				</div>
			)}

			<PersonalNote
				section="apps"
				note={note}
				editingNote={editingNote}
				noteText={noteText}
				onStartEdit={onStartEdit}
				onSave={onSaveNote}
				onCancel={onCancelNote}
				onChange={onChangeNote}
				accentColor={accentColor}
				accentColorLight={accentColorLight}
				placeholder="Ej: Ya tengo Grab instalada, falta Navitime..."
			/>
		</div>
	);
}

// ── Sección Dinero (con tipo de cambio en vivo) ──
function MoneySection({
	data,
	liveRate,
	rateLoading,
	note,
	editingNote,
	noteText,
	onStartEdit,
	onSaveNote,
	onCancelNote,
	onChangeNote,
}) {
	const { money, accentColor, accentColorLight } = data;
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			{/* Moneda + tipo de cambio en vivo */}
			<div
				style={{
					backgroundColor: accentColorLight,
					border: `2px solid ${accentColor}`,
					borderRadius: 16,
					padding: 16,
					textAlign: "center",
				}}
			>
				<CurrencyExchangeIcon sx={{ fontSize: 32, color: accentColor }} />
				<h3
					style={{
						...h4Style,
						color: accentColor,
						fontSize: 18,
						marginTop: 8,
					}}
				>
					{money.currency}
				</h3>

				{/* Tipo de cambio en vivo */}
				{liveRate ? (
					<div style={{ marginTop: 12 }}>
						<div
							style={{
								backgroundColor: "#fff",
								borderRadius: 12,
								padding: "12px 16px",
								border: `1px solid ${accentColor}30`,
								display: "inline-block",
							}}
						>
							<p
								style={{
									margin: 0,
									fontSize: 11,
									color: "#888",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 4,
								}}
							>
								<SyncIcon sx={{ fontSize: 14 }} /> Tipo de cambio en vivo
							</p>
							<p
								style={{
									margin: "6px 0 0",
									fontSize: 22,
									fontWeight: 800,
									color: accentColor,
								}}
							>
								1€ ={" "}
								{liveRate.rate.toLocaleString("es-ES", {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})}{" "}
								{liveRate.code}
							</p>
						</div>
						<p
							style={{
								...pStyle,
								fontSize: 10,
								color: "#aaa",
								marginTop: 6,
							}}
						>
							Actualizado:{" "}
							{new Date(liveRate.date).toLocaleDateString("es-ES", {
								day: "numeric",
								month: "short",
								year: "numeric",
							})}
						</p>
					</div>
				) : rateLoading ? (
					<p
						style={{
							...pStyle,
							marginTop: 8,
							fontSize: 12,
							color: "#888",
						}}
					>
						Cargando tipo de cambio...
					</p>
				) : (
					money.exchangeRate && (
						<p
							style={{
								...pStyle,
								fontSize: 14,
								fontWeight: 600,
								marginTop: 8,
							}}
						>
							{money.exchangeRate}
						</p>
					)
				)}

				{/* Conversor rápido */}
				{liveRate && (
					<QuickConverter
						rate={liveRate.rate}
						code={liveRate.code}
						accentColor={accentColor}
					/>
				)}
			</div>

			<InfoCard
				Icon={CreditCardIcon}
				title="Tarjetas"
				content={money.cards}
				accentColor={accentColor}
			/>
			<InfoCard
				Icon={LocalAtmIcon}
				title="Cajeros"
				content={money.atm}
				accentColor={accentColor}
			/>
			{money.tip && (
				<InfoCard
					Icon={TipsAndUpdatesIcon}
					title="Propinas"
					content={money.tip}
					accentColor={accentColor}
				/>
			)}

			<PersonalNote
				section="money"
				note={note}
				editingNote={editingNote}
				noteText={noteText}
				onStartEdit={onStartEdit}
				onSave={onSaveNote}
				onCancel={onCancelNote}
				onChange={onChangeNote}
				accentColor={accentColor}
				accentColorLight={accentColorLight}
				placeholder="Ej: Tarjeta Revolut, ya avisé al banco..."
			/>
		</div>
	);
}

// ── Mini conversor rápido ──
function QuickConverter({ rate, code, accentColor }) {
	const [euros, setEuros] = useState("");
	const converted = euros ? parseFloat(euros) * rate : 0;

	return (
		<div style={{ marginTop: 12 }}>
			<p style={{ ...pStyle, fontSize: 11, color: "#666", marginBottom: 6 }}>
				Conversor rápido
			</p>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 8,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
					<input
						type="number"
						inputMode="decimal"
						placeholder="10"
						value={euros}
						onChange={(e) => setEuros(e.target.value)}
						style={{
							width: 70,
							padding: "6px 8px",
							border: `2px solid ${accentColor}40`,
							borderRadius: 8,
							fontSize: 14,
							fontWeight: 600,
							textAlign: "center",
							outline: "none",
							fontFamily: "'Poppins', sans-serif",
						}}
					/>
					<span style={{ fontSize: 13, fontWeight: 600 }}>€</span>
				</div>
				<span style={{ fontSize: 13, color: "#888" }}>=</span>
				<span style={{ fontSize: 16, fontWeight: 700, color: accentColor }}>
					{converted.toLocaleString("es-ES", {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})}{" "}
					{code}
				</span>
			</div>
		</div>
	);
}

// ── Sección Idioma ──
function LanguageSection({
	data,
	note,
	editingNote,
	noteText,
	onStartEdit,
	onSaveNote,
	onCancelNote,
	onChangeNote,
}) {
	const { language, accentColor, accentColorLight } = data;
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
			<div
				style={{
					backgroundColor: accentColorLight,
					border: `2px solid ${accentColor}`,
					borderRadius: 16,
					padding: 16,
					textAlign: "center",
				}}
			>
				<TranslateIcon sx={{ fontSize: 32, color: accentColor }} />
				<h3
					style={{
						...h4Style,
						color: accentColor,
						fontSize: 18,
						marginTop: 8,
					}}
				>
					Idioma: {language.main}
				</h3>
				{language.english && (
					<p style={{ ...pStyle, marginTop: 8 }}>{language.english}</p>
				)}
			</div>
			{language.phrases.length > 0 && (
				<>
					<h4
						style={{
							...h4Style,
							marginTop: 4,
							marginBottom: 0,
							display: "flex",
							alignItems: "center",
							gap: 6,
						}}
					>
						<RecordVoiceOverIcon sx={{ fontSize: 18 }} /> Frases útiles
					</h4>
					{language.phrases.map((phrase, index) => (
						<div
							key={index}
							style={{
								backgroundColor: "#fff",
								borderRadius: 12,
								padding: "12px 14px",
								border: "2px solid #eee",
							}}
						>
							<p
								style={{
									...pStyle,
									fontWeight: 700,
									color: accentColor,
									fontSize: 14,
								}}
							>
								{phrase.phrase}
							</p>
							<p
								style={{
									...pStyle,
									fontSize: 12,
									color: "#666",
								}}
							>
								{phrase.meaning}
							</p>
						</div>
					))}
				</>
			)}

			<PersonalNote
				section="language"
				note={note}
				editingNote={editingNote}
				noteText={noteText}
				onStartEdit={onStartEdit}
				onSave={onSaveNote}
				onCancel={onCancelNote}
				onChange={onChangeNote}
				accentColor={accentColor}
				accentColorLight={accentColorLight}
				placeholder="Ej: Ya sé algo de japonés básico, practicar kanji..."
			/>
		</div>
	);
}

// ── Componente reutilizable: Nota personal ──
function PersonalNote({
	section,
	note,
	editingNote,
	noteText,
	onStartEdit,
	onSave,
	onCancel,
	onChange,
	accentColor,
	accentColorLight,
	placeholder,
}) {
	const isEditing = editingNote === section;
	const hasContent = note?.content?.trim();

	return (
		<div
			style={{
				backgroundColor: hasContent && !isEditing ? "#FFFBEB" : "#fff",
				border: `2px ${isEditing ? "solid" : "dashed"} ${
					isEditing ? accentColor : hasContent ? "#F59E0B" : "#ddd"
				}`,
				borderRadius: 12,
				padding: 12,
				transition: "all 0.2s",
			}}
		>
			{isEditing ? (
				<div>
					<h4
						style={{
							...h4Style,
							color: accentColor,
							marginBottom: 8,
							display: "flex",
							alignItems: "center",
							gap: 6,
						}}
					>
						<EditNoteIcon sx={{ fontSize: 18 }} /> Tu nota personal
					</h4>
					<textarea
						value={noteText}
						onChange={(e) => onChange(e.target.value)}
						placeholder={placeholder}
						autoFocus
						rows={3}
						style={{
							width: "100%",
							padding: "10px 12px",
							border: `2px solid ${accentColor}40`,
							borderRadius: 10,
							fontSize: 13,
							fontFamily: "'Poppins', sans-serif",
							outline: "none",
							resize: "vertical",
							boxSizing: "border-box",
							transition: "border-color 0.15s",
						}}
						onFocus={(e) => (e.target.style.borderColor = accentColor)}
						onBlur={(e) => (e.target.style.borderColor = `${accentColor}40`)}
					/>
					<div
						style={{
							display: "flex",
							gap: 8,
							marginTop: 8,
							justifyContent: "flex-end",
						}}
					>
						<button
							onClick={onSave}
							style={{
								padding: "6px 14px",
								border: "none",
								borderRadius: 8,
								backgroundColor: accentColor,
								color: "#fff",
								fontWeight: 600,
								fontSize: 12,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								gap: 4,
							}}
						>
							<CheckIcon sx={{ fontSize: 16 }} /> Guardar
						</button>
						<button
							onClick={onCancel}
							style={{
								padding: "6px 14px",
								border: "1px solid #ddd",
								borderRadius: 8,
								backgroundColor: "#fff",
								color: "#888",
								fontSize: 12,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
							}}
						>
							<CloseIcon sx={{ fontSize: 16 }} />
						</button>
					</div>
				</div>
			) : hasContent ? (
				<div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginBottom: 6,
						}}
					>
						<span
							style={{
								fontSize: 11,
								fontWeight: 600,
								color: "#F59E0B",
								display: "flex",
								alignItems: "center",
								gap: 4,
							}}
						>
							<EditNoteIcon sx={{ fontSize: 14 }} /> Tu nota
						</span>
						<button
							onClick={onStartEdit}
							style={{
								padding: "2px 8px",
								border: "1px solid #ddd",
								borderRadius: 6,
								backgroundColor: "#fff",
								color: "#888",
								fontSize: 10,
								cursor: "pointer",
							}}
						>
							Editar
						</button>
					</div>
					<p
						style={{
							...pStyle,
							fontSize: 12,
							color: "#666",
							whiteSpace: "pre-wrap",
						}}
					>
						{note.content}
					</p>
				</div>
			) : (
				<button
					onClick={onStartEdit}
					style={{
						width: "100%",
						padding: "8px",
						border: "none",
						backgroundColor: "transparent",
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 6,
						color: "#aaa",
						fontSize: 12,
						fontFamily: "'Poppins', sans-serif",
					}}
				>
					<EditNoteIcon sx={{ fontSize: 16 }} /> Añadir nota personal
				</button>
			)}
		</div>
	);
}

// ── Info Card reutilizable ──
function InfoCard({ Icon, title, content, accentColor }) {
	if (!content) return null;
	return (
		<div
			style={{
				backgroundColor: "#fff",
				borderRadius: 16,
				padding: 16,
				border: "2px solid #eee",
			}}
		>
			<h4
				style={{
					...h4Style,
					color: accentColor,
					display: "flex",
					alignItems: "center",
					gap: 6,
				}}
			>
				<Icon sx={{ fontSize: 18 }} /> {title}
			</h4>
			<p style={{ ...pStyle, whiteSpace: "pre-line" }}>{content}</p>
		</div>
	);
}

// Estilos compartidos
const h4Style = {
	fontFamily: "'Poppins', sans-serif",
	fontSize: 14,
	fontWeight: 700,
	margin: 0,
	marginBottom: 8,
};
const pStyle = {
	fontFamily: "'Poppins', sans-serif",
	fontSize: 13,
	color: "#444",
	margin: 0,
	lineHeight: 1.5,
};
